// Structured encounter note (S/O/A/P) — THU-346. The FB-authorized synthetic demo clinical store
// (Slack Jun 18); persists per patient via the local encounter-note client. This IS the cockpit's
// Encounter-notes column (it replaced the freeform huddle note). Single-column: S, O, A, P each a
// full-width row. Synthetic data only; see DECISIONS.md.
//
// ⚠️ This file legitimately carries clinical terms (Luan's hints/labels + "Save to chart") and is
// therefore explicitly allowlisted in scripts/check-clinical-terms.mjs. The rest of the app stays
// gated — do not copy these strings elsewhere without the same scoped carve-out.
import { useEffect, useState } from "react";
import { useEncounterNoteApi } from "../lib/encounterNoteApi";
import { useAsync } from "../lib/useAsync";

const ClipIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="15" y2="17" />
  </svg>
);
const SaveIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);

const EMPTY = { subjective: "", objective: "", assessment: "", plan: "" };
const snap = (n: typeof EMPTY) => JSON.stringify(n);

const FIELDS = [
  { key: "subjective", letter: "s", label: "Subjective", hint: "Chief complaint, HPI, ROS, what the patient reports", placeholder: "Patient reports…" },
  { key: "objective", letter: "o", label: "Objective", hint: "Vitals, exam findings, lab and imaging results", placeholder: "BP, HR, exam findings…" },
  { key: "assessment", letter: "a", label: "Assessment", hint: "Clinical impression, diagnosis, differential", placeholder: "Clinical impression…" },
  { key: "plan", letter: "p", label: "Plan", hint: "Treatment, medications, follow-up, patient education", placeholder: "Treatment plan…" },
] as const;

export function SoapPanel({ patientId }: { patientId: string }) {
  const api = useEncounterNoteApi();
  const { data, loading } = useAsync(() => api.get(patientId), [patientId]);
  const [note, setNote] = useState(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    const n = { subjective: data.subjective, objective: data.objective, assessment: data.assessment, plan: data.plan };
    setNote(n);
    if (data.status === "saved") setSavedSnapshot(snap(n));
    if (data.updated_at) setLastSaved(fmtTime(data.updated_at));
  }, [data]);

  const hasContent = Object.values(note).some((v) => v.trim());
  const isSaved = hasContent && savedSnapshot === snap(note);
  const set = (key: keyof typeof EMPTY, value: string) => setNote((n) => ({ ...n, [key]: value }));

  async function save() {
    if (!hasContent || busy) return;
    setBusy(true);
    try {
      const res = await api.save(patientId, { ...note, status: "saved" });
      setSavedSnapshot(snap(note));
      setLastSaved(fmtTime(res.updated_at ?? new Date().toISOString()));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <header className="pp-panel-head">
        <div className="pp-panel-title">{ClipIcon}<span>Encounter <em>notes</em></span></div>
        {hasContent && (
          <span className={`soap-status ${isSaved ? "saved" : "draft"}`}>{isSaved ? "Saved" : "Draft"}</span>
        )}
      </header>

      <div className="soap-fields">
        {FIELDS.map((f) => (
          <div className="soap-field" key={f.key}>
            <label><span className={`soap-letter ${f.letter}`}>{f.letter.toUpperCase()}</span>{f.label}</label>
            <div className="soap-hint">{f.hint}</div>
            <textarea
              value={loading ? "" : note[f.key]}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
            />
          </div>
        ))}
      </div>

      <div className="soap-foot">
        <div className="soap-meta">
          {isSaved ? (
            <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="20 6 9 17 4 12" /></svg>Saved{lastSaved ? ` at ${lastSaved}` : ""}</>
          ) : (
            <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>Not saved yet</>
          )}
        </div>
        <div className="soap-actions">
          <button className="btn btn-primary" onClick={() => void save()} disabled={!hasContent || isSaved || busy}>
            {SaveIcon}
            {busy ? "Saving…" : "Save to chart"}
          </button>
        </div>
      </div>
    </>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
